<template>
  <div class="page-business-intelligence-competition">
    <PageMainAction :label="$t('page.business-intelligence.competitor.add-dialog.title')" @click="openAddDialog" />
    <div class="__header">
      <div class="__header-actions">
        <BaseSearchBar
          class="__search-bar"
          :class="{ 'in-search-mode': isSearching }"
          v-model="search"
          name="search"
          type="text"
          :placeholder="$t('page.business-intelligence.competitor.search')"
        ></BaseSearchBar>
        <!-- Phones: the header's "+" (PageMainAction) instead. -->
        <BaseButton v-if="!isPhone" hasIconLeft @click.native="openAddDialog">
          <BaseIcon name="plus" />
          {{ $t('actions.add') }}
        </BaseButton>
      </div>
      <div ref="topAnchor" class="__results">
        <span class="__count">
          <span class="number-monospace">{{ formatLocalizedNumber(this.data.length) }}</span>
          {{ ' ' + $tc('page.business-intelligence.results', this.data.length) }}
        </span>
        <BaseRefreshButton :pending="pending" @refresh="fetchCompetitors()" />
      </div>
    </div>
    <div class="__content">
      <!-- Phones: a single tab is only a title, which the section and the count already give. -->
      <v-tabs v-if="!isPhone" v-model="activeTab" @change="onSelectedTabChange">
        <v-tab v-for="tab in availableTabs" :key="tab.key" :disabled="tab.disabled">
          <BaseIcon v-if="tab.icon" :name="tab.icon" />
          <span>{{ tab.name }}</span>
        </v-tab>
      </v-tabs>

      <TableState
        v-if="!hasResults"
        :state-loading="!hasResults && pending"
        :state-no-data="!hasResults && !pending"
        :state-text="pending ? $t('page.business-intelligence.loading') : $t('page.business-intelligence.no-result')"
      />
      <TableNormal
        item-key="id"
        v-if="data.length"
        v-model="selectedCompetitors"
        :fixed-header="true"
        :headers="headers"
        :items="data"
        :items-per-page="isSearching ? -1 : itemsPerPage"
        hide-default-footer
        class="bi-competitors-table"
        @click:row="goToCompetitor"
        :page.sync="currentPage"
        :search="search"
      >
        <template v-slot:item.name="{ item }">
          <div class="__row-name">
            <div>{{ item.name }}</div>
            <!-- Opens on hover, on keyboard focus and on a tap; its clicks stay off the row's navigation. -->
            <v-popover
              trigger="hover focus click"
              placement="top"
              popoverClass="tender-bottom-nav__nav-tooltip"
              @click.native.stop
            >
              <BaseTag
                class="__aliases"
                tone="neutral"
                preset="soft"
                icon="at-sign"
                role="button"
                tabindex="0"
                :aria-label="$tc(`${i18n}.alias-badge-label`, aliasTooltip(item).count, aliasTooltip(item))"
              >
                {{ item.competitorNames.length }}
              </BaseTag>
              <template v-slot:popover>
                <div class="bi-competitor-aliases-tooltip">
                  <div>{{ $tc(`${i18n}.alias-badge-tooltip`, aliasTooltip(item).count, aliasTooltip(item)) }}</div>
                  <ul v-if="aliasTooltip(item).shown.length">
                    <li v-for="name in aliasTooltip(item).shown" :key="name">{{ name }}</li>
                  </ul>
                  <div v-if="aliasTooltip(item).hidden">
                    {{ $t(`${i18n}.alias-badge-more`, { count: aliasTooltip(item).hidden }) }}
                  </div>
                </div>
              </template>
            </v-popover>
          </div>
        </template>
        <template v-slot:item.open_tenders="{ item }">
          <BaseLoadingValue :state-loading="detailedPending">
            <template slot="content">
              {{ item.open_tenders }}
            </template>
          </BaseLoadingValue>
        </template>
        <template v-slot:item.times_against="{ item }">
          <BaseLoadingValue :state-loading="detailedPending">
            <template slot="content">
              {{ item.times_against }}
            </template>
          </BaseLoadingValue>
        </template>
        <template v-slot:item.success_rate_against="{ item }">
          <BaseLoadingValue :state-loading="detailedPending">
            <template slot="content">
              {{
                item.success_rate_against >= 0 && item.times_against > 0
                  ? formatPercentageShort(item.success_rate_against)
                  : '-'
              }}
            </template>
          </BaseLoadingValue>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="__actions">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div @click.stop v-on="on" v-bind="attrs">
                  <BaseIcon name="more-horizontal" class="__more" />
                </div>
              </template>
              <div class="__actions-menu base-actions-menu">
                <ul>
                  <li @click="onExternalLinkClicked(item.id)">
                    <BaseIcon name="external-link" />
                    <span>{{ $t('page.business-intelligence.open-competitor-in-new-tab') }}</span>
                  </li>
                  <li @click="openEditDialog(item)">
                    <BaseIcon name="edit-2" />
                    <span>{{ $t('actions.edit') }}</span>
                  </li>
                  <li class="delete" @click="openDeleteDialog(item)">
                    <BaseIcon name="trash-2" />
                    <span>{{ $t('actions.delete') }}</span>
                  </li>
                </ul>
              </div>
            </v-menu>
          </div>
        </template>
      </TableNormal>
      <!-- Phones list every competitor (they all come at once): no pages. -->
      <div class="bi-competitors-table__pagination" v-if="totalPages > 1 && !isSearching && !isPhone">
        <TablePagination v-model="currentPage" :length="totalPages" @input="onChangePage" />
      </div>
    </div>
    <AddCompetitorDialog ref="addCompetitorDialog" @action-succeeded="onRefresh" />
    <EditCompetitorDialog ref="editCompetitorDialog" />
    <DeleteCompetitorDialog ref="deleteCompetitorDialog" />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { formatPercentageShort, formatLocalizedNumber } from '@/util/number-util';

import BaseTag from '@/components/base/BaseTag.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import TableState from '@/components/table/TableState.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseSearchBar from '@/components/base/BaseSearchBar.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import AddCompetitorDialog from './AddCompetitorDialog.vue';
import EditCompetitorDialog from './EditCompetitorDialog.vue';
import DeleteCompetitorDialog from './DeleteCompetitorDialog.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';

// How many aliases the badge's tooltip names before folding the rest into "and N more".
const ALIAS_TOOLTIP_MAX = 3;
const I18N = 'page.business-intelligence.competitor';

export default {
  name: 'PageBICompetition',
  components: {
    BaseMoreActions,
    PageMainAction,
    BaseRefreshButton,
    BaseTag,
    TableNormal,
    TableState,
    TablePagination,
    BaseLoadingValue,
    BaseButton,
    BaseSearchBar,
    BaseIcon,
    AddCompetitorDialog,
    EditCompetitorDialog,
    DeleteCompetitorDialog,
  },
  props: {},
  computed: {
    i18n: () => I18N,
    ...mapGetters('biDashboard', ['competitors', 'pending', 'detailedPending']),
    headers() {
      // todo: reenable once sorting on BE is fixed
      return [
        {
          text: this.$t('page.business-intelligence.competitor.data-table-titles.name'),
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('page.business-intelligence.competitor.data-table-titles.open-tenders'),
          value: 'open_tenders',
          sortable: false,
        },
        {
          text: this.$t('page.business-intelligence.competitor.data-table-titles.times-against'),
          value: 'times_against',
          sortable: false,
        },
        {
          text: this.$t('page.business-intelligence.competitor.data-table-titles.success-rate-against'),
          value: 'success_rate_against',
          sortable: false,
        },
        {
          text: this.$t('page.business-intelligence.competitor.data-table-titles.actions'),
          sortable: false,
          align: 'left',
          value: 'actions',
          width: '100px',
        },
      ];
    },
    totalPages() {
      return Math.ceil(this.competitors.length / this.itemsPerPage);
    },
    data() {
      return this.competitors;
    },
    hasResults() {
      return this.data.length > 0;
    },
    allAreSelected() {
      return this.data.length === this.selectedCompetitors.length;
    },
    hasSelection() {
      return this.selectedCompetitors.length > 0;
    },
    partiallySelected() {
      return this.hasSelection && this.data.length !== this.selectedCompetitors.length;
    },
    availableTabs() {
      return this.tabs.map((tab) => {
        return { ...tab, disabled: this.detailedPending };
      });
    },
    isSearching() {
      return this.search !== '';
    },
    isPhone() {
      return viewport.size === 'phone';
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        {
          key: 'open',
          label: this.$t('page.business-intelligence.open-competitor-in-new-tab'),
          icon: 'external-link',
        },
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'open') this.onExternalLinkClicked(item.id);
      else if (key === 'edit') this.openEditDialog(item);
      else this.openDeleteDialog(item);
    },
    // The badge shows the alias count; its tooltip says what the count is and lists the first few:
    // `count` of them, the `shown` names, and how many more are `hidden`.
    aliasTooltip(item) {
      const names = (item.competitorNames || []).map((name) => name.normalizedName);
      const shown = names.slice(0, ALIAS_TOOLTIP_MAX);
      return { count: names.length, shown, hidden: names.length - shown.length };
    },
    ...mapActions('biDashboard', ['getCompetitors', 'setCustomSortingField', 'setCustomSortingOrder']),
    formatPercentageShort,
    formatLocalizedNumber,
    fetchCompetitors(page = 1) {
      this.getCompetitors({
        page: page,
      });
    },
    onChangePage(page = 1) {
      this.fetchCompetitors(page);
    },
    onSelectedTabChange(tabIdx) {
      this.activeTab = tabIdx;
    },
    goToCompetitor(competitor) {
      this.$router.push({
        name: 'business-intelligence-competitor-performance',
        params: { competitorId: competitor.id },
        query: { name: competitor.name },
      });
    },
    openAddDialog() {
      this.$refs.addCompetitorDialog.open();
    },
    openEditDialog(competitor) {
      this.$refs.editCompetitorDialog.open(competitor);
    },
    openDeleteDialog(competitor) {
      this.$refs.deleteCompetitorDialog.open(competitor);
    },
    onRefresh() {
      this.fetchCompetitors();
    },
    onExternalLinkClicked(competitorId) {
      const routeData = this.$router.resolve({
        name: 'business-intelligence-competitor-performance',
        params: { competitorId },
      });
      window.open(routeData.href, '_blank', 'noopener,noreferrer');
    },
  },
  watch: {},
  data() {
    return {
      itemsPerPage: 25,
      currentPage: 1,
      activeTab: 0,
      selectedCompetitors: [],
      search: '',
      tabs: [{ key: 'all', name: this.$t('page.business-intelligence.competitor.tabs.all') }],
    };
  },
  async created() {
    // default sorting for competitors
    this.setCustomSortingField({ sortingField: 'name' });
    this.setCustomSortingOrder({ sortingOrder: 'asc' });
    await this.fetchCompetitors();
  },
};
</script>

<style lang="scss">
// The alias badge's popover is rendered at the body, outside the page's tree, so its rule stands alone.
.bi-competitor-aliases-tooltip {
  max-width: 240px;
  text-align: left;

  ul {
    margin: 4px 0;
    padding-left: 16px;
    list-style: disc;
  }
}

.page-business-intelligence-competition {
  padding: 32px;

  @include screen-below('tablet') {
    padding: $phone-page-gutter;
  }

  .__actions {
    display: flex;
    align-items: center;
    justify-content: flex--end;
    gap: 16px;
  }

  > .__header {
    padding-bottom: 16px;

    .__header-actions {
      display: flex;
      align-items: flex-end;
      padding-bottom: 16px;
      justify-content: space-between;
      gap: 8px;

      .__search-bar {
        &.in-search-mode {
          .base-icon {
            color: $color-accent;
          }
          .base-search-bar__input {
            border: 1px solid $color-accent;
          }
        }
      }
    }

    .__results {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .__count {
      color: $color-neutral;
      font-size: $font-size-small;
      padding: 4px 12px;
      text-transform: uppercase;
    }
  }

  > .__content {
    border-radius: $border-radius-root;

    > .v-tabs {
      border-top-left-radius: $border-radius-root;
      border-top-right-radius: $border-radius-root;

      .v-tab {
        text-transform: none;
        color: $color-normal-pale;

        &.v-tab--active {
          color: $color-normal;
          font-weight: 600;
        }
      }
    }
    .bi-competitors-table {
      tr th {
        font-size: $font-size-small !important;
      }
      tbody {
        td {
          color: $color-normal;

          &:first-child {
            padding-left: 16px !important;
          }

          .v-skeleton-loader {
            max-width: 100px;
          }

          .__more {
            cursor: pointer;
            margin-right: 24px;
          }
        }
      }

      .__row-name {
        display: flex;
        align-items: left;
        flex-direction: row;
        align-items: center;
        gap: 4px;
      }

      .__aliases {
        cursor: default;

        &:focus-visible {
          outline: 2px solid $color-accent;
          outline-offset: 2px;
        }
      }

      &__pagination {
        align-items: center;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        padding: 32px 32px 0;
        width: 100%;
      }
    }
  }
}
</style>
